<!-- Header omitted in embedded mode — the Gable drawer's own header
     carries the back-arrow + Teach Gable title in that case. -->
<div class="drawer-header" *ngIf="!embedded">
    <h3 class="drawer-title">
        <i class="ph-bold ph-graduation-cap icon-ai-purple icon-20" aria-hidden="true"></i>
        Teach Gable
        <app-super-star></app-super-star>
    </h3>
    <button mat-icon-button [disableRipple]="true" type="button" aria-label="Close" (click)="onCancel()">
        <i class="ph-bold ph-x icon-muted icon-18" aria-hidden="true"></i>
    </button>
</div>

<div class="drawer-body">
    <p class="lead" *ngIf="!isEdit">
        Write a short rule that tells Gable how to handle this kind of question. Next time someone asks
        something similar, the rule will be inlined into Gable's working context for that turn.
    </p>

    <div class="reference-card" *ngIf="!isEdit && data.userQuestion">
        <div class="reference-label">From this conversation</div>
        <div class="user-question">{{ data.userQuestion }}</div>
        <button
            *ngIf="data.assistantAnswer || (data.reasoningTrail && data.reasoningTrail.length > 0)"
            type="button"
            class="answer-disclosure"
            (click)="toggleAnswerDetails()">
            <i class="ph-bold icon-muted icon-12"
               [class.ph-caret-down]="!showAnswerDetails"
               [class.ph-caret-up]="showAnswerDetails"
               aria-hidden="true"></i>
            {{ showAnswerDetails ? 'Hide Gable\'s answer' : 'Show Gable\'s answer' }}
        </button>

        <div class="answer-details" *ngIf="showAnswerDetails">
            <div class="answer-body markdown-body"
                 *ngIf="data.assistantAnswer"
                 [innerHTML]="data.assistantAnswer | markdown"></div>
            <ul class="reasoning-list" *ngIf="data.reasoningTrail && data.reasoningTrail.length > 0">
                <li *ngFor="let line of data.reasoningTrail; trackBy: trackByIndex">{{ line }}</li>
            </ul>
        </div>
    </div>

    <form [formGroup]="form" class="teach-form">
        <div class="field-row">
            <label for="teach-trigger">When someone asks about a similar concept</label>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <textarea
                    id="teach-trigger"
                    matInput
                    formControlName="triggerContext"
                    rows="3"
                    placeholder="e.g. counting recurring templates, monthly dues, or auto-billing setup per unit"></textarea>
            </mat-form-field>
        </div>

        <div class="dup-alert" *ngIf="bestMatch as dup">
            <i class="ph-bold ph-warning icon-warning icon-20 dup-alert-icon" aria-hidden="true"></i>
            <div class="dup-alert-body">
                <div class="dup-alert-title">Similar lesson already exists</div>
                <div class="dup-alert-desc">
                    <button type="button" class="dup-ref" (click)="toggleDupPreview()">{{ dup.title }}</button>
                    covers similar ground. Consider editing it instead of creating a duplicate.
                </div>
                <button type="button"
                        class="dup-toggle"
                        [class.open]="showDupPreview"
                        (click)="toggleDupPreview()">
                    <span>{{ showDupPreview ? 'Hide preview' : 'Preview lesson' }}</span>
                    <i class="ph-bold ph-caret-down icon-primary icon-14" aria-hidden="true"></i>
                </button>

                <div class="dup-expand" *ngIf="showDupPreview">
                    <div class="dup-preview-card">
                        <div class="dup-preview-label">Trigger phrase</div>
                        <div class="dup-preview-text">{{ dup.triggerContext }}</div>
                    </div>
                    <div class="dup-preview-card">
                        <div class="dup-preview-label">Correct answer</div>
                        <div class="dup-preview-text">{{ dup.guidance }}</div>
                    </div>
                    <div class="dup-actions">
                        <button
                            type="button"
                            mat-flat-button
                            color="primary"
                            [disableRipple]="true"
                            (click)="editThisLessonInstead(dup)">
                            Edit this lesson instead
                        </button>
                        <button
                            type="button"
                            mat-flat-button
                            color="accent"
                            [disableRipple]="true"
                            (click)="keepMyVersion()">
                            Keep my version
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="field-row">
            <label for="teach-title">Give this rule a short name</label>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <input
                    id="teach-title"
                    matInput
                    type="text"
                    formControlName="title"
                    placeholder="e.g. Recurring templates per unit" />
            </mat-form-field>
        </div>

        <div class="field-row">
            <label for="teach-guidance">Explain the correct answer the way you'd brief a new teammate</label>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <textarea
                    id="teach-guidance"
                    matInput
                    formControlName="guidance"
                    rows="6"
                    placeholder="e.g. Count DISTINCT active templates per unit, not raw junction rows."></textarea>
            </mat-form-field>
            <div class="char-counter" [class.over-limit]="guidanceOverLimit">
                {{ guidanceLength }} / {{ guidanceMaxChars }}
            </div>
        </div>

        <div class="field-row">
            <label>Supporting screenshots <span class="optional-tag">(optional)</span></label>

            <!-- Existing summaries (read-only) — only when editing a lesson
                 that already has summarized screenshots. The raw images
                 aren't stored, so we just show the text descriptions. -->
            <ul class="existing-screenshot-summaries"
                *ngIf="existingScreenshotSummaries.length > 0">
                <li *ngFor="let s of existingScreenshotSummaries; trackBy: trackByIndex">{{ s }}</li>
            </ul>

            <app-gable-screenshot-uploader
                [value]="pendingScreenshots"
                [maxImages]="2"
                (valueChange)="pendingScreenshots = $event">
            </app-gable-screenshot-uploader>
            <span class="field-hint">
                Pasted screenshots are converted to a short text description so we don't
                store the raw images. Max 2 per lesson.
            </span>
        </div>

        <div class="test-block">
            <div class="test-block-header">
                <div class="test-heading">
                    <i class="ph-bold ph-flask icon-ai-purple icon-18" aria-hidden="true"></i>
                    Test before you save
                </div>
                <button
                    mat-flat-button
                    type="button"
                    color="accent"
                    [disableRipple]="true"
                    [disabled]="!canTest"
                    (click)="onTestLesson()">
                    <ng-container *ngIf="!isTesting">Test this lesson</ng-container>
                    <ng-container *ngIf="isTesting">
                        <i class="ph-bold ph-circle-notch icon-muted icon-16 spinning" aria-hidden="true"></i>
                        Re-asking Gable…
                    </ng-container>
                </button>
            </div>

            <div class="test-result" *ngIf="testAnswer !== null">
                <div class="test-result-tag">
                    <i class="ph-bold ph-check-circle icon-success icon-16" aria-hidden="true"></i>
                    Gable's new answer
                </div>
                <div class="test-result-body markdown-body" [innerHTML]="testAnswer | markdown"></div>
                <p class="test-result-hint">
                    Looks right? Save it. Want to tweak? Edit the guidance and test again.
                </p>
            </div>

            <div class="test-error" *ngIf="testError">{{ testError }}</div>
        </div>
    </form>

    <app-alert-block *ngIf="errorMessage" type="danger" class="error-alert">
        {{ errorMessage }}
    </app-alert-block>
</div>

<div class="drawer-footer">
    <button
        *ngIf="isEdit"
        mat-flat-button
        type="button"
        class="retire-btn"
        [disableRipple]="true"
        [disabled]="isRetiring"
        (click)="onRetire()">
        <i class="ph-bold ph-archive icon-error icon-16" aria-hidden="true"></i>
        {{ isRetiring ? 'Retiring…' : 'Retire' }}
    </button>

    <flex-spacer></flex-spacer>

    <button
        mat-flat-button
        type="button"
        color="accent"
        class="ghost-btn"
        [disableRipple]="true"
        (click)="onCancel()">
        Cancel
    </button>

    <button
        mat-flat-button
        type="button"
        color="primary"
        [disableRipple]="true"
        [disabled]="!canSave"
        (click)="onSubmit()">
        <i class="ph-bold ph-graduation-cap icon-white icon-16" aria-hidden="true"></i>
        {{ submitLabel }}
    </button>
</div>
